import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import "./ContactServicePicker.css";

export const CONTACT_SERVICES = [
  { label: "Performance Creative", subject: "Performance Creative Inquiry", message: "I'd like to discuss a performance creative project with Bonvi." },
  { label: "Explainer Video", subject: "Explainer Video Inquiry", message: "I'd like to discuss an explainer video project with Bonvi." },
  { label: "Social Media and PR", subject: "Social Media & PR Inquiry", message: "I'd like to discuss social media and PR services with Bonvi." },
  { label: "Web & Digital Development", subject: "Web & Digital Development Inquiry", message: "I'd like to discuss a web or digital development project with Bonvi." },
  { label: "Other", subject: "Other Inquiry", message: "I'd like to discuss a project with Bonvi." },
] as const;

export type ContactService = (typeof CONTACT_SERVICES)[number];

type Props = { selectedService: ContactService | null; onChange: (service: ContactService) => void; compact?: boolean };

export default function ContactServicePicker({ selectedService, onChange, compact = false }: Props) {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className={`contact-service-picker${compact ? " contact-service-picker--compact" : ""}`} ref={pickerRef}>
      <h2>Choose what would you like to create</h2>
      <button type="button" className="contact-service-picker__trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        <span>{selectedService?.label ?? "None"}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      <div className="contact-service-picker__menu" role="listbox" aria-label="Choose a service" hidden={!open}>
        {CONTACT_SERVICES.map((service) => (
          <button type="button" role="option" aria-selected={selectedService?.label === service.label} key={service.label} onClick={() => { onChange(service); setOpen(false); }}>
            {service.label}
          </button>
        ))}
      </div>
    </div>
  );
}
